import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { ChatMessage, loadingByKey } from '../../types/Chat';

interface ChatState {
  chatId: string | null;
  messages: ChatMessage[];
  loadingByKey: Record<string, loadingByKey>;
  currentLoadingKey: string | null;
  createdAt: string | null;
  streamingChunks: Record<string, string>; // ← novo
}

const initialState: ChatState = {
  chatId: null,
  messages: [],
  loadingByKey: {},
  currentLoadingKey: null,
  createdAt: null,
  streamingChunks: {}, // ← novo
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setChatId(state, action: PayloadAction<string | null>) {
      state.chatId = action.payload;

      if (action.payload) {
        state.currentLoadingKey = action.payload;
      }
    },
    setMessages(state, action: PayloadAction<ChatMessage[]>) {
      state.messages = action.payload;
    },
    addMessages(state, action: PayloadAction<ChatMessage[]>) {
      state.messages = [...state.messages, ...action.payload];
    },
    setLoadingKey(
      state,
      action: PayloadAction<{
        key: string;
        status: 'streaming' | 'done' | 'error';
        text?: string | null;
        phase?: string | null;
        progressStartedAt?: string | null;
      }>,
    ) {
      const { key, status } = action.payload;
      const prev = state.loadingByKey[key] ?? {
        status: 'streaming' as const,
        text: null,
        phase: null,
        progressStartedAt: null,
      };

      const next = { ...prev, status };

      if (Object.prototype.hasOwnProperty.call(action.payload, 'text')) {
        next.text = action.payload.text ?? null;
      }

      if (Object.prototype.hasOwnProperty.call(action.payload, 'phase')) {
        next.phase = action.payload.phase ?? null;
      }

      if (
        Object.prototype.hasOwnProperty.call(action.payload, 'progressStartedAt')
      ) {
        next.progressStartedAt = action.payload.progressStartedAt ?? null;
      }

      state.loadingByKey[key] = next;

      if (status === 'streaming') {
        state.currentLoadingKey = key;
      }

      if (status === 'done' || status === 'error') {
        state.loadingByKey[key].text = null;
        state.loadingByKey[key].phase = null;
        state.loadingByKey[key].progressStartedAt = null;
      }

      if (status === 'error' && state.currentLoadingKey === key) {
        state.currentLoadingKey = null;
      }
    },

    setCreatedAt(state, action: PayloadAction<string | null>) {
      state.createdAt = action.payload;
    },
    setCurrentLoadingKey(state, action: PayloadAction<string | null>) {
      state.currentLoadingKey = action.payload;
    },
    clearChat(
      state,
      action: PayloadAction<{ preserveLoading?: boolean } | undefined>,
    ) {
      const preserveLoading = action.payload?.preserveLoading ?? false;
      state.chatId = null;
      state.messages = [];
      state.createdAt = null;
      if (!preserveLoading) {
        state.loadingByKey = {};
        state.currentLoadingKey = null;
        state.streamingChunks = {};
      }
    },

    // Acumula token por token durante o streaming
    appendStreamChunk(
      state,
      action: PayloadAction<{ chatId: string; chunk: string }>,
    ) {
      const { chatId, chunk } = action.payload;
      state.streamingChunks[chatId] = (state.streamingChunks[chatId] ?? '') + chunk;
    },

    // Limpa o buffer quando o streaming termina (ao receber done: true)
    clearStreamChunk(state, action: PayloadAction<string>) {
      delete state.streamingChunks[action.payload];
    },
  },
});

export const {
  setChatId,
  setMessages,
  addMessages,
  setLoadingKey,
  setCurrentLoadingKey,
  setCreatedAt,
  clearChat,
  appendStreamChunk, // ← exportar
  clearStreamChunk, // ← exportar
} = chatSlice.actions;

export default chatSlice.reducer;
